/**
 * JWT payload decoding.
 *
 * JWT segments are base64url-encoded (RFC 7515 §2): `-` and `_` replace `+`
 * and `/`, and `=` padding is omitted. `atob()` only accepts standard base64,
 * so `atob(token.split(".")[1])` throws `InvalidCharacterError` whenever a
 * payload happens to encode to a `-` or `_` — which depends on the exact
 * claim bytes (names, titles, …), so it fails deterministically for a few
 * users and never for most. That bug left an affected user's every API call
 * without a token (endless loading on boot). Always decode JWTs through here.
 */

/** `name` of every error thrown here — a data/programming bug, never a network blip. */
export const JWT_DECODE_ERROR_NAME = "JwtDecodeError";

function jwtDecodeError(message: string): Error {
  const error = new Error(message);
  error.name = JWT_DECODE_ERROR_NAME;
  return error;
}

/** True when `error` came from decoding a JWT here. */
export function isJwtDecodeError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === JWT_DECODE_ERROR_NAME;
}

/** Convert base64url (or standard base64, padded or not) to padded standard base64. */
function toStandardBase64(segment: string): string {
  const base64 = segment
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .replace(/=+$/, "");
  return base64 + "=".repeat((4 - (base64.length % 4)) % 4);
}

/** Decode a JWT's payload (middle segment) as UTF-8 JSON. */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const segment = token.split(".")[1];
  if (!segment) {
    throw jwtDecodeError("Token has no payload segment");
  }
  try {
    const binary = atob(toStandardBase64(segment));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw jwtDecodeError("Token payload is not valid base64url JSON");
  }
}

/** The token's `exp` claim in epoch milliseconds. */
export function getJwtExpiryMs(token: string): number {
  const { exp } = decodeJwtPayload(token);
  if (typeof exp !== "number") {
    throw jwtDecodeError("Token payload has no numeric exp claim");
  }
  return exp * 1000;
}
